import { type RefObject, useCallback, useState } from 'react';

import type { ItemMeta, RichTreeItem } from '../../types';
import { findPathToNode } from '../../utils/treeItem';

export type MoveDialogTarget = {
  moveNodeId: string;
  moveNodeMeta: ItemMeta;
  expandedAncestors: string[];
};

type UseRequestMoveArgs = {
  treeItemsRef: RefObject<RichTreeItem[]>;
  itemMetaMapRef: RefObject<Record<string, ItemMeta>>;
};

export function useRequestMove({ treeItemsRef, itemMetaMapRef }: UseRequestMoveArgs) {
  const [moveTarget, setMoveTarget] = useState<MoveDialogTarget | null>(null);

  const requestMove = useCallback(
    (treeId: string) => {
      const meta = itemMetaMapRef.current?.[treeId];
      if (!meta) return;

      // Ancestor chain (node excluded) is read from the main tree, where the
      // node is necessarily visible, and handed to the dialog to seed its own
      // expansion. The main tree's expansion is intentionally left untouched.
      const path = findPathToNode(treeItemsRef.current ?? [], treeId);
      const ancestors = path ? path.slice(0, -1) : [];

      setMoveTarget({ moveNodeId: treeId, moveNodeMeta: meta, expandedAncestors: ancestors });
    },
    [itemMetaMapRef, treeItemsRef]
  );

  const closeMoveDialog = useCallback(() => setMoveTarget(null), []);

  return { moveTarget, requestMove, closeMoveDialog };
}
